import React from "react";
import { Dialog, DialogContent, Button, AppBar, Toolbar, Typography, IconButton, Box, useTheme, styled, Divider, ThemeProvider } from "@mui/material";
import { Close as CloseIcon, Print as PrintIcon } from "@mui/icons-material";
import { formatDateTime, formatDate } from "../../../helpers/utils";
import EnergiesPieChart from "./EnergiesPieChart";
import EnergiesTableContentPDF from "./EnergyTableContentPDF";
import PieChartIcon from '@mui/icons-material/PieChart';
import GridViewIcon from '@mui/icons-material/GridView';
import { muiLightTheme } from "../../../../theme/muiTheme";

const EnergyReportPrintVersion = ({
  isOpen,
  onClose,
  reportData,
  systemName,
  loading,
  t,
  reportType,
  subReportGroupSelection,
  heatmapOn,
  piechartOn,
  togglePieChart,
  toggleHeatmap
}) => {

  console.log("reportData in print version:", reportData);

  const handlePrint = () => {
    window.print();
  };

  const theme = useTheme();

  // Calculate time period from first and last reports
  const getTimePeriod = () => {
    if (!reportData || reportData.length === 0) return '';

    const firstReport = reportData[reportData.length - 1];
    const lastReport = reportData[0];

    if (reportType === "Month") {
      // For month reports, show first day of first month to first day of last month
      const firstDate = `01.${("0" + (new Date(firstReport.ReportDate).getMonth() + 1)).slice(-2)}.${firstReport.ReportDate.substring(0, 4)}`;
      const lastDate = `01.${("0" + (new Date(lastReport.ReportDate).getMonth() + 1)).slice(-2)}.${lastReport.ReportDate.substring(0, 4)}`;
      return `${firstDate} - ${lastDate}`;
    } else {
      // For day reports, show actual date range
      const firstDate = formatDate(firstReport.ReportDate);
      const lastDate = formatDate(lastReport.ReportDate);
      return `${firstDate} - ${lastDate}`;
    }
  };

  const StyledTypography = styled(Typography)({
    color: 'black',
    '@media print': {
      color: 'black !important'
    }
  });



  return (
    <Dialog
      fullScreen
      open={isOpen}
      onClose={onClose}
      sx={{ backgroundColor: 'white' }}
    >
      <AppBar sx={{
        '@media print': {
          display: 'none',
        },
        position: 'relative',
        backgroundColor: muiLightTheme.palette.kontram.primary
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Energy Report - Print View
          </Typography>
          <Button
            onClick={togglePieChart}
            startIcon={<PieChartIcon />}
            color="inherit"
            sx={{ mx: 1 }}
          >
            {piechartOn ? "Pie Chart On" : "Pie Chart Off"}
          </Button>
          <Divider orientation="vertical" flexItem />
          <Button
            onClick={toggleHeatmap}
            startIcon={<GridViewIcon />}
            color="inherit"
            sx={{ mx: 1 }}
          >
            {heatmapOn ? "Heatmap On" : "Heatmap Off"}
          </Button>
          <Divider orientation="vertical" flexItem />
          <Button
            color="inherit"
            onClick={handlePrint}
            startIcon={<PrintIcon />}
            sx={{ mx: 1 }}
          >
            Print
          </Button>
        </Toolbar>
      </AppBar>
      <ThemeProvider theme={muiLightTheme}>
        <DialogContent sx={{
          padding: 3,
          minHeight: '100vh',
          backgroundColor: 'white',
          '@media print': {
            padding: 1
          }
        }}>
          <StyledTypography
            variant="h4"
            sx={{ textAlign: 'center', mb: 1 }}
          >
            Energies Report - {reportType}
          </StyledTypography>
          <StyledTypography
            variant="h5"
            sx={{ mb: 1, textAlign: 'center' }}
          >
            {systemName}
          </StyledTypography>

          <StyledTypography
            variant="h6"
            sx={{ mb: 3, textAlign: 'center', fontStyle: 'italic', color: 'text.secondary' }}
          >
            Time Period: {getTimePeriod()}
          </StyledTypography>

          {subReportGroupSelection && (
            <StyledTypography
              variant="h6"
              sx={{ mb: 2, textAlign: 'center' }}
            >
              Selected Group: {subReportGroupSelection}
            </StyledTypography>
          )}

          {loading && (
            <StyledTypography variant="h6" sx={{ textAlign: 'center' }}>
              Loading...
            </StyledTypography>
          )}

          {!loading && reportData && reportData.length > 0 && (
            <>
              {reportData.map((singleReport, index) => {
                const month = new Date(singleReport.ReportDate).getMonth() + 1;

                return (
                  <Box
                    key={`print-report-${index}-${singleReport.ReportDate}`}
                    sx={{
                      '@media print': {
                        pageBreakBefore: 'always',
                        '&:last-child': {
                          pageBreakAfter: 'avoid'
                        }
                      }
                    }}
                  >
                    <StyledTypography
                      variant="h5"
                      sx={{ mb: 0.5, textAlign: 'center', fontWeight: 'bold' }}
                    >
                      {reportType === "Month" ?
                        `Month Energy Report ${("0" + month).slice(-2)}.${singleReport.ReportDate.substring(0, 4)}`
                        :
                        `Day Energy Report ${formatDate(singleReport.ReportDate)}`
                      }
                    </StyledTypography>

                    <Box sx={{
                      display: 'flex',
                      flexDirection: "column",
                      gap: 2,
                    }}>
                      <Box sx={{
                        flex: 1,
                        minWidth: 0,
                      }}
                      >
                        <EnergiesTableContentPDF
                          singleReport={singleReport}
                          reportType={reportType}
                          month={month}
                          subReportGroupSelection={subReportGroupSelection}
                          heatmapOn={heatmapOn}
                        />
                      </Box>

                      {piechartOn && (
                        <Box sx={{
                          '@media print': {
                            mt: 2,
                            pageBreakBefore: 'always',
                            '& canvas': {
                              maxWidth: '300px !important',
                              height: 'auto !important'
                            }
                          }
                        }}>
                          <EnergiesPieChart
                            reportData={singleReport}
                            subReportGroupSelection={subReportGroupSelection}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </>
          )}
        </DialogContent>
      </ThemeProvider>
    </Dialog>
  )
}

export default EnergyReportPrintVersion